<template>
    <loading-indicator v-if="loading"/>
    <table v-else class="portuna-table">
        <colgroup>
            <col style="width: 165px;"/>
            <col/>
            <col/>
            <col/>
            <col/>
            <col style="width: 45px;"/>
            <col style="width: 70px;"/>
        </colgroup>
        <thead>
        <tr>
            <th class="text-center background-dark">
                <i aria-hidden="true" class="material-icons md-20 table-icon contrast-primary-light" role="img">timelapse</i>
            </th>
            <th>Mitarbeiter</th>
            <th class="text-center">Zeitraum</th>
            <th class="text-center">Antrag</th>
            <th class="text-center">Erstellt</th>
            <th></th>
            <th></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(entry, index) of openRequests">
            <td>
                <i aria-hidden="true" class="material-icons md-20 table-icon contrast-primary-light" role="img">
                    {{requestType[entry.type].icon}}
                </i>
                {{requestType[entry.type].tooltip}}
            </td>
            <td>
                <person-box-employee :id="entry.employee" :key="`employee_${entry.employee}`" #name="{username}" class="tiny">
                    <router-link :to="{name: 'employee-view', params: {id: entry.employee}}">
                        <strong>{{username}}</strong>
                    </router-link>
                </person-box-employee>
            </td>
            <td class="text-center">
                {{entry.period}}
            </td>
            <td class="text-center">
                {{entry.request_info}}
            </td>
            <td class="text-center">
                {{entry.request_date}}
            </td>

            <td class="text-center">
                <span v-if="entry.sub_type.includes('revoke')" aria-label="Antrag zurückziehen">
                    <i v-tooltip aria-hidden="true" class="material-icons text-warning" role="img" title="Antrag zurückgezogen">undo</i>
                </span>
                <span v-if="entry.sub_type.includes('change')" aria-label="Antrag bearbeitet">
                    <i v-tooltip aria-hidden="true" class="material-icons" role="img" title="Antrag bearbeitet">hourglass_empty</i>
                </span>
            </td>
            <td class="text-center">
                <span v-interact="() => requestType[entry.type].cmd(index, entry, true)" aria-label="Antrag annehmen" class="icon-interact">
                    <i v-tooltip aria-hidden="true" class="material-icons text-success" role="img" title="Antrag annehmen">check_circle</i>
                </span>
                <span v-interact="() => requestType[entry.type].cmd(index, entry, false)" aria-label="Antrag ablehnen" class="icon-interact">
                    <i v-tooltip aria-hidden="true" class="material-icons text-danger" role="img" title="Antrag ablehnen">remove_circle</i>
                </span>
            </td>
        </tr>
        </tbody>
    </table>
</template>
<script lang="ts">
    import LoadingIndicator from '@/components/LoadingIndicator.vue';
    import TableSearch from '@/components/TableSearch.vue';
    import {ABSENCE_STATE_APPROVED, ABSENCE_STATE_DECLINED} from '@/library/modules/absences/absences';
    import {POPUP_ACTION_MODE_BOTH} from '@/library/modules/time-tracker/time-tracker';
    import {popupDefaultCancelButton} from '@/library/popup';
    import TopMessageHandler from '@/library/topMessageHandler';
    import {SearchAbstract} from '@/mixins/SearchAbstract';
    import {errorHandler} from '@/utils/portuna';
    import AbsenceRequestChangeStatusPopup from '@/views/absences/AbsenceRequestChangeStatusPopup.vue';
    import CorrectionEditPopup from '@/views/time-tracker/correction/CorrectionEditPopup.vue';
    import CreditViewPopup from '@/views/time-tracker/overtime/CreditViewPopup.vue';
    import OvertimePopup from '@/views/time-tracker/overtime/OvertimePopup.vue';
    import VacationRequestEditPopup from '@/views/vacation/entry/VacationRequestEditPopup.vue';
    import {markRaw} from 'vue';
    import {Component} from 'vue-facing-decorator';
    import {Action, Mutation} from 'vuex-facing-decorator';
    import dateHelper from '../utils/portuna/dateHelper';
    import ActionMenu from './ActionMenu.vue';
    import AppPopup from './AppPopup.vue';
    import AppSearchInput from './AppSearchInput.vue';
    import PersonBoxEmployee from './PersonBoxEmployee.vue';
    import PersonBoxUser from './PersonBoxUser.vue';
    import PhoneNumber from './PhoneNumber.vue';

    interface IOpenRequest
    {
        id: string;
        employee: string;
        period: string;
        request_info: string;
        request_date: string;
        start: string;
        type: string;
        sub_type: string;
        data?: any;
    }

    @Component({
        name: 'OpenRequestPopup',
        components: {
            LoadingIndicator,
            TableSearch,
            PersonBoxEmployee,
            ActionMenu,
            PhoneNumber,
            PersonBoxUser,
            AppPopup,
            AppSearchInput,
        },
        methods: {
            dateHelper,
        },
    })
    export default class OpenRequestPopup extends SearchAbstract
    {
        @Mutation('telephoneToolbar/decreaseOpenRequestCount') decreaseOpenRequestCount: (amount) => void;

        @Action('createPopup') createPopup: (options) => Promise<AppPopup>;

        @Action('changePopup') changePopup: (options: { id: string, values: Record<string, any> }) => void;

        openRequests: IOpenRequest[] = [];

        isOpen: boolean = false;

        requestType: Nullable<Record<string, {
            icon: string,
            tooltip: string,
            cmd: (index: number, entry: IOpenRequest, action?: string) => void,
            type: string,
        }>> = null;

        loading: boolean = false;

        get buttons(): PopupButton[]
        {
            return [
                popupDefaultCancelButton,
            ];
        }

        created()
        {
            this.requestType = {
                vacation: {
                    icon: 'flight',
                    tooltip: 'Urlaub',
                    cmd: this.editVacation,
                    type: 'edit',
                },
                overtime: {
                    icon: 'assignment',
                    tooltip: 'Stundenantrag',
                    cmd: this.editOvertime,
                    type: 'edit',
                },
                credit: {
                    icon: 'access_time',
                    tooltip: 'Stundengutschrift',
                    cmd: this.editCredit,
                    type: 'edit',
                },
                correction: {
                    icon: 'edit',
                    tooltip: 'Korrektur',
                    cmd: this.editCorrection,
                    type: 'edit',
                },
                absence: {
                    icon: 'block',
                    tooltip: 'Abwesenheit',
                    cmd: this.editAbsence,
                    type: 'decision',
                },
            };
            this.loading     = true;
            this.axios.get('/employee/time-tracker/index/fetch-open-requests').then(({data: {data}}) =>
            {
                this.openRequests = data.entries;
            }).catch(errorHandler).finally(() =>
            {
                this.loading = false;
            });

            document.documentElement.classList.add('open-telephone-popup');
        }

        closePopup()
        {
            document.documentElement.classList.remove('open-telephone-popup');
        }

        async editCorrection(index: number, entry: IOpenRequest, action: boolean)
        {
            let popup = await this.createPopup({
                title: `Korrektur bearbeiten`,
                titleIcon: 'edit',
                size: 'medium',
                component: markRaw(CorrectionEditPopup),
                componentProps: {
                    id: entry.id,
                    employee: entry.employee,
                    events: {
                        saved: () =>
                        {
                            this.handleEditedRequest(index);
                            popup.close();
                        },
                    },
                },
            });

            popup.open().then();
        }

        async editCredit(index: number, entry: IOpenRequest, action: boolean)
        {
            const popup = await this.createPopup({
                title: 'Antrag für Stundengutschrift',
                titleIcon: 'access_time',
                size: 'small',
                component: markRaw(CreditViewPopup),
                componentProps: {
                    id: entry.id,
                    events: {
                        update: () =>
                        {
                            TopMessageHandler.success('Der Antrag wurde erfolgreich bearbeitet.');
                            this.handleEditedRequest(index);
                            popup.close();
                        },
                    },
                }
            });

            popup.open().then();
        }

        async editVacation(index: number, entry: IOpenRequest, action: boolean)
        {
            const popup = await this.createPopup(
                {
                    title: (entry.sub_type === 'requested-revoke' ? 'Zurückziehung ' : '') + `Urlaubsantrag`,
                    titleIcon: 'flight_takeoff',
                    size: 'medium',
                    id: 'vacation_popup',
                    component: markRaw(VacationRequestEditPopup),
                    componentProps: {
                        entry: entry.data,
                        action: action ? 'accept' : 'decline',
                        revokeRequest: entry.sub_type === 'requested-revoke',
                        events: {
                            submit: () =>
                            {
                                this.handleEditedRequest(index);
                                popup.close();
                            },
                            decline: () =>
                            {
                                this.changePopup({
                                    id: 'vacation_popup',
                                    values: {'componentProps.action': 'decline'},
                                });
                            },
                        },
                    },
                },
            );

            popup.open().then();
        }

        async editOvertime(index: number, entry: IOpenRequest, action: boolean)
        {
            const title = {
                'requested': 'Stundenantrag bearbeiten',
                'requested-change': 'Änderungsantrag bearbeiten',
                'requested-revoke': 'Rückzug bearbeiten',
            };

            const popup = await this.createPopup({
                title: title[entry.sub_type],
                titleIcon: 'edit',
                size: 'small',
                component: markRaw(OvertimePopup),
                componentProps: {
                    id: entry.id,
                    request: false,
                    change: entry.sub_type === 'requested-change',
                    revoke: entry.sub_type === 'requested-revoke',
                    type: null,
                    actionType: POPUP_ACTION_MODE_BOTH,
                    events: {
                        update: () =>
                        {
                            this.handleEditedRequest(index);
                            popup.close();
                        },
                    },
                },
            });

            popup.open().then();
        }

        async editAbsence(index: number, entry: IOpenRequest, action: boolean)
        {
            const popup = await this.createPopup({
                title: 'Abwesenheitsantrag ' + (action ? 'annehmen' : 'ablehnen'),
                titleIcon: 'block',
                size: 'small',
                component: markRaw(AbsenceRequestChangeStatusPopup),
                componentProps: {
                    entry: entry.data,
                    accept: action,
                    newState: action ? ABSENCE_STATE_APPROVED : ABSENCE_STATE_DECLINED,
                    events: {
                        reload: () =>
                        {
                            this.handleEditedRequest(index);
                            popup.close();
                        },
                    },
                },
            });

            popup.open().then();
        }

        handleEditedRequest(index: number)
        {
            this.openRequests.splice(index, 1);
            this.decreaseOpenRequestCount(1);
        }
    }
</script>